/**
 * Material icon ligature for a file, chosen by its extension (#21239, task 21243).
 *
 * One canonical mapping: the read-view Upload card shows this glyph where a non-image file has no
 * thumbnail, and the upload/download form fields render the same icon in their file lists. Material
 * ligatures (the Material Icons font is loaded in index.html) give the distinctive PDF / Excel /
 * Word glyphs that the heroicons set does not carry.
 *
 * Images are intentionally NOT special-cased here — a caller that can show a real thumbnail does so
 * instead; one that cannot (or wants a placeholder) gets `image` for an image extension.
 */
const ICON_BY_EXTENSION: Record<string, string> = {
  '.pdf': 'picture_as_pdf',
  '.doc': 'description',
  '.docx': 'description',
  '.xls': 'grid_on',
  '.xlsx': 'grid_on',
  '.ppt': 'slideshow',
  '.pptx': 'slideshow',
  '.zip': 'archive',
  '.rar': 'archive',
  '.mp4': 'movie',
  '.avi': 'movie',
  '.mov': 'movie',
  '.mp3': 'audio_file',
  '.wav': 'audio_file',
  '.jpg': 'image',
  '.jpeg': 'image',
  '.png': 'image',
  '.gif': 'image',
  '.webp': 'image',
  '.svg': 'image',
  '.bmp': 'image',
};

/** The extension, including the leading dot and lower-cased, or `''` when the name has none. */
export function fileExtension(fileName: string | null | undefined): string {
  const dot = fileName?.lastIndexOf('.') ?? -1;
  return dot >= 0 ? fileName!.slice(dot).toLowerCase() : '';
}

/** Material icon ligature for the file, falling back to a generic document glyph. */
export function fileTypeIcon(fileName: string | null | undefined): string {
  return ICON_BY_EXTENSION[fileExtension(fileName)] ?? 'insert_drive_file';
}

/**
 * Whether a browser can show the file as an `<img>` — the extensions mapped to `image` above, so
 * the read view's Upload card (thumbnail or icon?) and this icon table can never disagree.
 */
export function isImageFile(fileName: string | null | undefined): boolean {
  return ICON_BY_EXTENSION[fileExtension(fileName)] === 'image';
}
